import { baseDataZoom, EChartsOption } from 'volta/components/bv-chart';
import { getColor } from 'volta/utils/style-utils';
import { makeNumberFormatter } from 'volta/utils/text-utils';

import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type IntlService from 'ember-intl/services/intl';
import type { IDemandForecastTs } from 'volta/services/adu-ts-data';

type TForecastKeys = keyof Omit<IDemandForecastTs, 'dates' | 'demandVolatility'>;
type TSeriesKey = TForecastKeys | 'aduMin' | 'aduOverride';
type TSeriesData = Record<TSeriesKey, [string | number, number][]>;

const SeriesZero = () => {
  return {
    demandQty: [],
    forecastQty: [],
    adu: [],
    aduMin: [],
    aduOverride: []
  };
};

const SeriesOptions = {
  demandQty: { type: 'area', label: 'demand', color: getColor('secondary') },
  forecastQty: { type: 'area', label: 'forecast', color: getColor('warning') },
  adu: { type: 'line', label: 'adu', color: getColor('danger') },
  aduOverride: { type: 'line', label: 'aduOverride', color: getColor('danger') },
  aduMin: { type: 'line', label: 'aduMin', color: getColor('danger') }
};

interface IArgs {
  forecasts: IDemandForecastTs;
  loading: boolean;
  aduMin?: number;
  aduOverride?: number;
}

export default class AduChart extends Component<IArgs> {
  @service intl!: IntlService;

  get forecasts() {
    return this.args.forecasts ?? {};
  }

  get hasAduMin() {
    return typeof this.args.aduMin === 'number';
  }
  get hasAduOverride() {
    return typeof this.args.aduOverride === 'number';
  }
  get hideAdu() {
    return this.hasAduMin || this.hasAduOverride;
  }

  @cached
  get data(): TSeriesData {
    const data: TSeriesData = { ...SeriesZero() };
    const { forecasts, hideAdu, hasAduMin, hasAduOverride } = this;
    const { aduMin, aduOverride } = this.args;

    forecasts.dates?.forEach((date, index) => {
      const dateUnix = new Date(date).getTime();
      ['demandQty', 'forecastQty'].concat(hideAdu ? [] : ['adu']).forEach((k: TForecastKeys) => {
        data[k].push([dateUnix, Math.abs(forecasts[k][index])]);
      });
      if (hasAduMin) {
        data['aduMin'].push([dateUnix, Math.abs(aduMin!)]);
      }
      if (hasAduOverride) {
        data['aduOverride'].push([dateUnix, Math.abs(aduOverride!)]);
      }
    });

    return data;
  }

  get chartOptions(): EChartsOption {
    const { data } = this;

    const legendData: TSeriesKey[] = ['demandQty', 'forecastQty'];

    if (!this.hideAdu) {
      legendData.push('adu');
    }

    if (this.hasAduMin) {
      legendData.push('aduMin');
    }

    if (this.hasAduOverride) {
      legendData.push('aduOverride');
    }

    return {
      legend: {
        data: legendData.map((k) => this.intl.t(SeriesOptions[k].label))
      },
      xAxis: {
        data: this.forecasts.dates
      },
      yAxis: {
        axisLabel: {
          formatter: makeNumberFormatter('shortScale', this.intl)
        }
      },
      dataZoom: baseDataZoom(),
      series: Object.keys(data).map((k: TSeriesKey) => {
        const opts = SeriesOptions[k];
        const isArea = opts.type === 'area';
        const areaStyle = isArea ? { color: opts.color } : undefined;
        return {
          name: this.intl.t(opts.label),
          data: data[k],
          type: 'line',
          showSymbol: false,
          color: opts.color,
          step: isArea && 'middle',
          lineStyle:
            k === 'aduMin'
              ? {
                  type: [4, 4]
                }
              : undefined,
          areaStyle,
          emphasis: {
            lineStyle: {
              color: opts.color,
              opacity: 0.8
            },
            areaStyle
          }
        };
      })
    };
  }
}
